import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { serviceRequestAPI, publicProgramAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  Check,
  Loader2,
  Clock,
  Star,
  MapPin,
  Calendar,
  DollarSign,
  Globe,
  GraduationCap,
  CreditCard,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  Shield,
  ChevronRight,
  BookOpen,
  MessageCircle,
  Users,
  Zap,
  CheckCircle2,
  HelpCircle,
  ListChecks,
  Package,
} from 'lucide-react';

// Hub sub-components
import ProcessStepper from '../../components/services/hub/ProcessStepper';
import Deliverables from '../../components/services/hub/Deliverables';
import FAQAccordion from '../../components/services/hub/FAQAccordion';
import SocialProof from '../../components/services/hub/SocialProof';
import Prerequisites from '../../components/services/hub/Prerequisites';
import RelatedServices from '../../components/services/hub/RelatedServices';
import TrackingDashboard from '../../components/services/hub/TrackingDashboard';
import ProfileAssessmentModal from '../../components/services/hub/ProfileAssessmentModal';
import ServiceFormModal from '../../components/services/hub/ServiceFormModal';
import TicketTravelBookingCard from '../../components/services/hub/TicketTravelBookingCard';

// Data
import { getServiceBySlug, getThemeBySlug } from '../../data/serviceData';
import ServiceLandingPageskeleton from '@/components/skeleton/ServiceLandingPageskeleton';

export default function ServiceLandingPage() {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const service = getServiceBySlug(serviceSlug);
  const theme = getThemeBySlug(serviceSlug);

  const [isApplied, setIsApplied] = useState(false);
  const [serviceRequestId, setServiceRequestId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [prerequisitesReady, setPrerequisitesReady] = useState(false);
  const [showPrereqWarning, setShowPrereqWarning] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showServiceFormModal, setShowServiceFormModal] = useState(false);
  const [serviceFormPrefill, setServiceFormPrefill] = useState(null);
  const [serviceFormUiPrefill, setServiceFormUiPrefill] = useState(null);

  // Program prefill state (only used for apply-university slug)
  const [program, setProgram] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);
  const programId = new URLSearchParams(location.search).get('programId');

  // Reset all state when switching between service pages so stale
  // values from the previous slug never leak into the new one.
  useEffect(() => {
    setIsApplied(false);
    setServiceRequestId(null);
    setApplying(false);
    setLoading(true);
    setPrerequisitesReady(false);
    setShowPrereqWarning(false);
    setShowAssessmentModal(false);
    setShowServiceFormModal(false);
    setServiceFormPrefill(null);
    setServiceFormUiPrefill(null);
    setProgram(null);

    if (!service) {
      setLoading(false);
      return;
    }
    fetchApplicationStatus();
    if (serviceSlug === 'apply-university' && programId) {
      fetchProgram(programId);
    }
  }, [serviceSlug]);

  const fetchProgram = async id => {
    setProgramLoading(true);
    try {
      const data = await publicProgramAPI.getById(id);
      setProgram(data?.data || null);
    } catch {
      setProgram(null);
    } finally {
      setProgramLoading(false);
    }
  };

  const fetchApplicationStatus = async () => {
    try {
      const response = await serviceRequestAPI.getAll();
      const applications = response.serviceRequests || response.data || [];
      const matchedApp = applications.find(
        app => app.serviceType === service.type && app.status !== 'CANCELLED',
      );
      if (matchedApp) {
        setIsApplied(true);
        setServiceRequestId(matchedApp.serviceRequestId || matchedApp._id);
      }
    } catch (error) {
      console.error('Failed to fetch application status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Core apply logic — creates the service request via API
  // formData param is optional: passed by ServiceFormModal for the 7 services,
  // omitted when ProfileAssessmentModal triggers (profile data lives on Student model).
  const executeApply = async (formData, representativeName) => {
    setApplying(true);
    try {
      const metadata = {};
      if (programId && program) {
        metadata.programId = programId;
        if (program.universityId) metadata.universityId = program.universityId;
        metadata.programName = program.programName;
        metadata.universityName = program.universityName;
        metadata.country = program.country;
      }
      const result = await serviceRequestAPI.create(
        service.type,
        metadata,
        formData || null,
        representativeName,
      );
      toast.success(
        serviceSlug === 'ticket-travel'
          ? 'Your Ticket & Travel Support request has been created. Our team will contact you soon.'
          : 'Service application submitted successfully!',
      );
      setIsApplied(true);
      setServiceRequestId(
        result.serviceRequest?.serviceRequestId || result.serviceRequest?._id,
      );
      setShowAssessmentModal(false);
      setShowServiceFormModal(false);
    } catch (error) {
      console.error('Apply error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to apply for service',
      );
      throw error; // re-throw so the modal can handle it
    } finally {
      setApplying(false);
      setShowPrereqWarning(false);
    }
  };

  const handleApply = async () => {
    // For profile-assessment, open multi-step form instead of applying directly
    if (serviceSlug === 'profile-assessment') {
      setShowAssessmentModal(true);
      return;
    }

    // For all other 7 services, open the pre-application form modal
    setShowServiceFormModal(true);
  };

  const handleTicketTravelApply = bookingData => {
    setServiceFormPrefill(bookingData?.formData || null);
    setServiceFormUiPrefill(bookingData?.uiState || null);
    setShowServiceFormModal(true);
  };

  // ─── Loading state ────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <ServiceLandingPageskeleton />
      </DashboardLayout>
    );
  }

  // ─── Not Found ────────────────────────────────────────────────
  if (!service) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center space-y-5">
            <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-sm">
              <Globe className="w-10 h-10 text-gray-300" />
            </div>
            <div>
              <p className="text-gray-800 font-bold text-xl font-outfit">
                Service not found
              </p>
              <p className="text-gray-400 text-sm mt-1.5 max-w-xs mx-auto">
                The service you're looking for doesn't exist or may have been
                moved.
              </p>
            </div>
            <Button
              className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 hover:-translate-y-0.5 transition-all duration-300 font-semibold"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const ServiceIcon = service.icon;

  // ═══════════════════════════════════════════════════════════════
  // WORKSPACE MODE — Student has applied, page IS the workspace
  // ═══════════════════════════════════════════════════════════════
  if (isApplied && serviceRequestId) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto">
          <TrackingDashboard
            serviceRequestId={serviceRequestId}
            serviceData={service}
            theme={theme}
          />
        </div>
      </DashboardLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // BROCHURE MODE — Student hasn't applied yet, show service info
  // ═══════════════════════════════════════════════════════════════
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto pb-12 space-y-6">
        {/* ═══ Program Preview (apply-university only) ═══ */}
        {serviceSlug === 'apply-university' && programId && (
          <div className="px-4 sm:px-6">
            <div
              className="rounded-2xl border p-5 sm:p-6"
              style={{
                borderColor: `${theme.accent}25`,
                background: `linear-gradient(135deg, ${theme.lightBg}80, white)`,
              }}
            >
              {programLoading ? (
                <div className="flex items-center gap-3 text-sm text-gray-500 py-4">
                  <Loader2
                    className="w-5 h-5 animate-spin"
                    style={{ color: theme.accent }}
                  />
                  Loading selected program...
                </div>
              ) : program ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${theme.accent}15` }}
                    >
                      <GraduationCap
                        className="w-4.5 h-4.5"
                        style={{ color: theme.accent }}
                      />
                    </div>
                    <span
                      className="text-xs font-bold uppercase tracking-wider"
                      style={{ color: theme.accent }}
                    >
                      Selected Program
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 leading-snug font-outfit">
                    {program.programName}
                  </h3>
                  <div className="flex flex-wrap gap-2.5 text-sm text-gray-600">
                    {[
                      {
                        icon: MapPin,
                        text: `${program.universityName}${program.location ? `, ${program.location}` : ''}`,
                      },
                      { icon: Globe, text: program.country },
                      ...(program.intake?.length > 0
                        ? [
                            {
                              icon: Calendar,
                              text: Array.isArray(program.intake)
                                ? program.intake.join(', ')
                                : program.intake,
                            },
                          ]
                        : []),
                      ...(program.tuitionFee
                        ? [
                            {
                              icon: DollarSign,
                              text: `$${Number(program.tuitionFee).toLocaleString()} tuition`,
                            },
                          ]
                        : []),
                    ].map(({ icon: Icon, text }, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${theme.accent}08`,
                          color: '#4B5563',
                        }}
                      >
                        <Icon
                          className="w-3.5 h-3.5"
                          style={{ color: theme.accent }}
                        />
                        {text}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400">
                    Pre-filled from your selected program — included in your
                    application.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-2">
                  Program details could not be loaded — you can still apply and
                  provide program info manually.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ═══ HERO SECTION ═══ */}
        {serviceSlug === 'ticket-travel' ? (
          <TicketTravelBookingCard
            applying={applying}
            onApply={handleTicketTravelApply}
            service={service}
          />
        ) : (
          <>
          <div className="px-4 sm:px-6">
          <div
            className="relative overflow-hidden rounded-[28px] backdrop-blur-xl"
            style={{
              background: `linear-gradient(135deg, ${theme.accent}25 0%, ${theme.accent}45 50%, ${theme.accent}20 100%)`,
              border: `1.5px solid ${theme.accent}35`,
            }}
          >
            {/* Decorative orbs */}
            <div
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ backgroundColor: theme.accent }}
            />
            <div
              className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full blur-3xl opacity-15 pointer-events-none"
              style={{ backgroundColor: theme.accent }}
            />
            <div className="absolute inset-0 rounded-[28px] bg-white/5 pointer-events-none" />

            <div className="relative px-6 py-4 sm:px-8 sm:py-5 lg:px-10 lg:py-6">
              {/* Two-column: left content / right image */}
              <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
                {/* ── Left column ── */}
                <div className="flex-1 space-y-5">
                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    {service.popular && (
                      <span
                        className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] font-bold tracking-wide text-white shadow-md"
                        style={{
                          background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`,
                          boxShadow: `0 4px 14px ${theme.accent}30`,
                        }}
                      >
                        <Star className="w-3 h-3 fill-white" />
                        POPULAR
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-3.5 py-1 rounded-full text-[11px] font-semibold text-gray-600 shadow-sm border border-white/60">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {service.duration}
                    </span>
                  </div>

                  {/* Icon + title + tagline */}
                  <div className="flex items-start gap-4">
                    <div
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden backdrop-blur-md"
                      style={{
                        background: `linear-gradient(135deg, ${theme.accent}35 0%, ${theme.accent}15 100%)`,
                        border: `1.5px solid ${theme.accent}30`,
                        boxShadow: `
        0 8px 28px ${theme.accent}20,
        inset 0 1px 0 rgba(255,255,255,0.4)
      `,
                      }}
                    >
                      {service.heroIcon ? (
                        <img
                          src={service.heroIcon}
                          alt={service.name}
                          className="w-10 h-10 sm:w-11 sm:h-11 object-contain drop-shadow-md"
                        />
                      ) : (
                        <ServiceIcon
                          className="w-7 h-7 sm:w-8 sm:h-8"
                          style={{
                            color: theme.accent,
                            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))',
                          }}
                        />
                      )}
                    </div>

                    <div>
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 font-outfit tracking-tight leading-tight">
                        {service.name}
                      </h1>
                      <p className="text-gray-500 text-sm sm:text-base leading-relaxed mt-2 max-w-md font-jakarta">
                        {service.tagline}
                      </p>
                    </div>
                  </div>

                  {/* Trust pills */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 bg-emerald-50/80 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Expert Support
                    </span>
                    <span className="inline-flex items-center gap-1 bg-blue-50/80 text-blue-700 px-3 py-1 rounded-full text-[11px] font-semibold">
                      <Shield className="w-3.5 h-3.5" />
                      Secure
                    </span>
                    <span className="inline-flex items-center gap-1 bg-amber-50/80 text-amber-700 px-3 py-1 rounded-full text-[11px] font-semibold">
                      <Zap className="w-3.5 h-3.5" />
                      Fast
                    </span>
                  </div>

                  {/* Apply CTA */}
                  <Button
                    onClick={handleApply}
                    disabled={applying}
                    className="rounded-2xl px-10 py-4 h-auto text-white font-bold text-[15px] tracking-wide shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] active:translate-y-0 active:scale-100 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:scale-100 border-0 cursor-pointer group"
                    style={{
                      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}dd)`,
                      boxShadow: `0 12px 40px ${theme.accent}30`,
                    }}
                  >
                    {applying ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        Apply Now
                        <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                </div>

                {/* ── Right column — service image ── */}
                {service.heroImage && (
                  <div className="lg:w-[45%] shrink-0">
                    <div className="relative rounded-2xl overflow-hidden">
                      <img
                        src={service.heroImage}
                        alt={service.name}
                        className="w-full object-contain"
                      />
                      {/* Subtle gradient overlay at bottom */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `linear-gradient(to top, ${theme.accent}10 0%, transparent 40%)`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ QUICK STATS BAR ═══ */}
        </>
        )}
        <div className="px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              {
                icon: Users,
                label: 'Students Served',
                value: service.stats?.studentsServed || '500+',
              },
              {
                icon: Star,
                label: 'Satisfaction',
                value: service.stats?.satisfaction || '98%',
              },
              {
                icon: Clock,
                label: 'Avg. Completion',
                value: service.stats?.avgCompletion || '2 weeks',
              },
              {
                icon: Zap,
                label: 'Quick Start',
                value: 'Same Day',
              },
            ].map(({ icon: Icon, label, value }, i) => (
              <div
                key={i}
                className="rounded-2xl p-4 sm:p-5 flex items-center gap-3 sm:gap-4 backdrop-blur-sm hover:-translate-y-0.5 transition-all duration-300 group cursor-default"
                style={{
                  background: `linear-gradient(135deg, ${theme.accent}08 0%, ${theme.accent}15 100%)`,
                  border: `1px solid ${theme.accent}18`,
                }}
              >
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${theme.accent}25, ${theme.accent}10)`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: theme.accent }} />
                </div>
                <div>
                  <p className="text-lg sm:text-xl font-extrabold text-gray-900 font-outfit leading-none">
                    {value}
                  </p>
                  <p className="text-[10px] sm:text-[11px] text-gray-400 font-semibold mt-1 uppercase tracking-wide">
                    {label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ SECTION: How It Works ═══ */}
        <div
          className="mx-4 sm:mx-6 rounded-[28px] p-6 sm:p-8 backdrop-blur-sm"
          style={{
            background: `linear-gradient(135deg, ${theme.accent}08 0%, ${theme.accent}15 50%, ${theme.accent}06 100%)`,
            border: `1.5px solid ${theme.accent}18`,
          }}
        >
          <SectionHeader
            icon={ListChecks}
            title="How It Works"
            subtitle="Your step-by-step journey from application to completion"
            accentColor={theme.accent}
          />
          <div
            className="rounded-2xl p-6 sm:p-8 backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.75)',
              border: `1px solid ${theme.accent}10`,
            }}
          >
            <ProcessStepper
              steps={service.processSteps}
              activeStep={-1}
              theme={theme}
            />
          </div>
        </div>

        {/* ═══ SECTION: What's Included ═══ */}
        <div
          className="mx-4 sm:mx-6 rounded-[28px] p-6 sm:p-8 backdrop-blur-sm"
          style={{
            background: `linear-gradient(135deg, ${theme.accent}06 0%, ${theme.accent}12 50%, ${theme.accent}05 100%)`,
            border: `1.5px solid ${theme.accent}18`,
          }}
        >
          <SectionHeader
            icon={Package}
            title="What's Included"
            subtitle="Everything you get with this service package"
            accentColor={theme.accent}
          />
          <div
            className="rounded-2xl p-6 sm:p-8 backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.75)',
              border: `1px solid ${theme.accent}10`,
            }}
          >
            <Deliverables deliverables={service.deliverables} theme={theme} />
          </div>
        </div>

        {/* ═══ SECTION: FAQ ═══ */}
        <div
          className="mx-4 sm:mx-6 rounded-[28px] p-6 sm:p-8 backdrop-blur-sm"
          style={{
            background: `linear-gradient(135deg, ${theme.accent}06 0%, ${theme.accent}14 50%, ${theme.accent}05 100%)`,
            border: `1.5px solid ${theme.accent}18`,
          }}
        >
          <SectionHeader
            icon={HelpCircle}
            title="Frequently Asked Questions"
            subtitle="Quick answers to help you decide with confidence"
            accentColor={theme.accent}
          />
          <div
            className="rounded-2xl p-6 sm:p-8 backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.75)',
              border: `1px solid ${theme.accent}10`,
            }}
          >
            <FAQAccordion faqs={service.faqs} theme={theme} />
          </div>
        </div>

        {/* ═══ SECTION: Social Proof ═══ */}
        <div
          className="mx-4 sm:mx-6 rounded-[28px] p-6 sm:p-8 backdrop-blur-sm"
          style={{
            background: `linear-gradient(135deg, ${theme.accent}08 0%, ${theme.accent}16 50%, ${theme.accent}06 100%)`,
            border: `1.5px solid ${theme.accent}18`,
          }}
        >
          <SectionHeader
            icon={MessageCircle}
            title="What Students Say"
            subtitle="Real feedback from students who used this service"
            accentColor={theme.accent}
          />
          <div
            className="rounded-2xl p-6 sm:p-8 backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.75)',
              border: `1px solid ${theme.accent}10`,
            }}
          >
            <SocialProof
              stats={service.stats}
              testimonials={service.testimonials}
              theme={theme}
            />
          </div>
        </div>

        {/* ═══ SECTION: Prerequisites ═══ */}
        <div
          className="mx-4 sm:mx-6 rounded-[28px] p-6 sm:p-8 backdrop-blur-sm"
          style={{
            background: `linear-gradient(135deg, ${theme.accent}06 0%, ${theme.accent}14 50%, ${theme.accent}05 100%)`,
            border: `1.5px solid ${theme.accent}18`,
          }}
        >
          <SectionHeader
            icon={CheckCircle2}
            title="What You'll Need"
            subtitle="Prepare these items before applying — you can still apply without everything ready"
            accentColor={theme.accent}
          />
          <div
            className="rounded-2xl p-6 sm:p-8 backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.75)',
              border: `1px solid ${theme.accent}10`,
            }}
          >
            <Prerequisites
              items={service.prerequisites}
              theme={theme}
              onAllChecked={ready => setPrerequisitesReady(ready)}
            />
          </div>
        </div>

        {/* ═══ CTA SECTION ═══ */}
        <div className="px-4 sm:px-6">
          <div
            className="relative overflow-hidden rounded-[28px] backdrop-blur-xl"
            style={{
              background: `linear-gradient(135deg, ${theme.accent}20 0%, ${theme.accent}35 50%, ${theme.accent}18 100%)`,
              border: `1.5px solid ${theme.accent}30`,
            }}
          >
            {/* Decorative orbs */}
            <div
              className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ backgroundColor: theme.accent }}
            />
            <div
              className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full blur-3xl opacity-15 pointer-events-none"
              style={{ backgroundColor: theme.accent }}
            />
            <div className="absolute inset-0 rounded-[28px] bg-white/5 pointer-events-none" />

            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                {/* Left content */}
                <div className="flex-1 space-y-4">
                  <span
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em]"
                    style={{ color: theme.accent }}
                  >
                    <Sparkles className="w-4 h-4" />
                    Your Journey Starts Here
                  </span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 font-outfit leading-tight">
                    Ready to get started?
                  </h2>
                  <p className="text-gray-500 text-sm sm:text-base leading-relaxed max-w-lg font-jakarta">
                    Apply for{' '}
                    <span className="font-semibold text-gray-700">
                      {service.name}
                    </span>{' '}
                    starting at{' '}
                    <span className="font-semibold text-gray-700">
                      {service.price}
                    </span>
                    . After applying, this page transforms into your personal
                    workspace — track progress, chat with your counselor, upload
                    documents, and more.
                  </p>

                  {/* Trust badges */}
                  <div className="flex flex-wrap gap-2.5 pt-1">
                    {[
                      { icon: Shield, text: 'Secure Payment' },
                      { icon: MessageCircle, text: 'Counselor Chat' },
                      { icon: Zap, text: 'Instant Access' },
                    ].map(({ icon: Icon, text }, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold backdrop-blur-sm"
                        style={{
                          background: 'rgba(255,255,255,0.6)',
                          color: theme.accent,
                          border: `1px solid ${theme.accent}15`,
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {text}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Right CTA */}
                <div className="flex flex-col items-center gap-4 shrink-0">
                  <Button
                    onClick={handleApply}
                    disabled={applying}
                    className="rounded-2xl px-12 py-4 h-auto text-white font-bold text-base tracking-wide shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] active:translate-y-0 active:scale-100 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:scale-100 border-0 cursor-pointer group"
                    style={{
                      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}dd)`,
                      boxShadow: `0 12px 40px ${theme.accent}30`,
                    }}
                  >
                    {applying ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        Apply Now
                        <ChevronRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>

                  {showPrereqWarning && !prerequisitesReady && (
                    <span
                      className="flex items-center gap-1.5 text-xs font-medium text-amber-700 px-4 py-2 rounded-full"
                      style={{
                        background: 'rgba(255,251,235,0.8)',
                        border: '1px solid rgba(251,191,36,0.2)',
                      }}
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Some prerequisites unchecked — processing may take longer
                    </span>
                  )}

                  <div className="flex items-center gap-2 text-gray-400">
                    <CreditCard className="w-4 h-4" />
                    <span className="text-[11px] font-medium">
                      Visa, Mastercard, bKash accepted
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ SECTION: Related Services ═══ */}
        {service.relatedServices?.length > 0 && (
          <div
            className="mx-4 sm:mx-6 rounded-[28px] p-6 sm:p-8 backdrop-blur-sm"
            style={{
              background: `linear-gradient(135deg, ${theme.accent}06 0%, ${theme.accent}12 50%, ${theme.accent}05 100%)`,
              border: `1.5px solid ${theme.accent}18`,
            }}
          >
            <SectionHeader
              icon={Sparkles}
              title="You Might Also Need"
              subtitle="Explore complementary services to complete your journey"
              accentColor={theme.accent}
            />
            <RelatedServices relatedSlugs={service.relatedServices} />
          </div>
        )}
      </div>

      {/* Profile Assessment multi-step modal */}
      {serviceSlug === 'profile-assessment' && (
        <ProfileAssessmentModal
          open={showAssessmentModal}
          onOpenChange={setShowAssessmentModal}
          onApplySuccess={executeApply}
        />
      )}

      {/* Pre-application form modal for all other 7 services */}
      {serviceSlug !== 'profile-assessment' && (
        <ServiceFormModal
          open={showServiceFormModal}
          onOpenChange={setShowServiceFormModal}
          serviceSlug={serviceSlug}
          onSubmit={executeApply}
          program={program}
          initialFormData={
            serviceSlug === 'ticket-travel' ? serviceFormPrefill : null
          }
          initialUiState={
            serviceSlug === 'ticket-travel' ? serviceFormUiPrefill : null
          }
        />
      )}
    </DashboardLayout>
  );
}

/* ─── Section Header ─── */
function SectionHeader({ icon: Icon, title, subtitle, accentColor }) {
  return (
    <div className="flex items-start gap-4 mb-6">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 backdrop-blur-sm"
        style={{
          background: `linear-gradient(135deg, ${accentColor}25, ${accentColor}10)`,
          border: `1px solid ${accentColor}15`,
        }}
      >
        <Icon className="w-5 h-5" style={{ color: accentColor }} />
      </div>
      <div>
        <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 font-outfit leading-snug">
          {title}
        </h2>
        <p className="text-sm text-gray-400 mt-1 font-jakarta leading-relaxed">
          {subtitle}
        </p>
      </div>
    </div>
  );
}
